/*!
 * vue-reactive-store v0.0.17
 * (c) Mathieu DARTIGUES
 * Released under the MIT License.
 */
import Vue from 'vue';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0;
    var v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
}
/**
 * Wrapper for action,
 * allow for trigger hooks
 */


var hookWrapper = function hookWrapper(store, key, func, hooks) {
  return _async(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    var wrapperId = uuidv4(); // wait for initial mutation of store

    return _await(Vue.nextTick(), function () {
      // call all before hooks
      // and memorize result to pass it to the after hook
      hooks.forEach(function (hook) {
        hook.before && hook.before(store, key, wrapperId);
      }); // we call the initial function with parameters

      return _await(func.apply(void 0, args), function () {
        // we wait for the next DOM update (and so the state mutations)
        return _await(Vue.nextTick(), function () {
          // call all after hooks
          hooks.forEach(function (hook) {
            hook.after && hook.after(store, key, wrapperId);
          });
        });
      });
    });
  });
};

/**
 * Vue Reactive Store,
 * based on VueJS reactivity system.
 * Inspired by VueX and Vue.js instance.
 */

var VueReactiveStore =
/*#__PURE__*/
function () {
  /**
   * Reactive store based on VueJS reactivity system
   *
   * @param {VRSStore} store
   * The store, composed of :
   * * name
   * * state
   * * computed properties
   * * actions potentially async
   * * watchers
   * * modules, aka sub-stores (wip)
   * * plugins that could be trigerred before / after store evolution
   * @param {VRSStore} rootStore
   * Used only for internal purpose (plugins),
   * reference to the root store.
   */
  function VueReactiveStore(store, rootStore) {
    var _this = this;

    _classCallCheck(this, VueReactiveStore);

    this.name = '';
    this.state = {};
    this.actions = {};
    this.computed = {};
    this.watch = {};
    this.plugins = [];
    this.modules = {};
    this.subStores = {};
    if (!store) throw new Error('Please provide a store to VueReactiveStore');
    this.name = store.name || 'my-store';
    this.state = store.state || {};
    this.computed = store.computed || {};
    this.actions = store.actions || {};
    this.watch = store.watch || {};
    this.plugins = [];

    if (store.plugins) {
      store.plugins.forEach(function (p) {
        if (typeof p === 'function') {
          _this.plugins.push(p(_this));
        } else {
          _this.plugins.push(p);
        }
      });
    }

    this.modules = store.modules || {};
    this.rootStore = rootStore || this; // check if each module doesn't exist in store state
    // or in a computed property
    // to correctly namespace them

    Object.keys(this.modules).forEach(function (moduleName) {
      if (_this.state[moduleName]) {
        var errorMessage = "\n          You're trying to add a module which its name already exist as a state property.\n          Please rename your module or your state property.\n          (Store ".concat(_this.name, ", property/module ").concat(moduleName, ")\n        ");
        throw new Error(errorMessage);
      } // if (this.computed[moduleName]) {
      //   const errorMessage = `
      //     You're trying to add a module which its name already exist as a computed property.
      //     Please rename your module or your computed property.
      //     (Store ${this.name}, computed/module ${moduleName})
      //   `
      //   throw new Error(errorMessage)
      // }


      _this.subStores[moduleName] = new VueReactiveStore(Object.assign({}, _this.modules[moduleName], {
        name: _this.name + '.modules.' + (_this.modules[moduleName].name || moduleName),
        plugins: (_this.modules[moduleName].plugins || []).concat(_this.plugins)
      }), _this.rootStore);
    }); // group all actions hooks available, from global and local plugins

    var actionsHooks = VueReactiveStore.globalPlugins.map(function (hook) {
      return Object.assign({}, hook.actions);
    }).concat(this.plugins.map(function (hook) {
      return Object.assign({}, hook.actions);
    }));
    Object.keys(this.actions).forEach(function (key) {
      _this.actions[key] = hookWrapper(_this.rootStore, _this.name + '.actions.' + key, _this.actions[key], actionsHooks);
    }); // create a Vue instance
    // to use the VueJS reactivity system

    this._vm = new Vue({
      data: function data() {
        return _this.state;
      },
      computed: store.computed,
      watch: store.watch
    }); // listen to state mutations of current store
    // doesn't work for modules because they will be added after
    // knowing each modules has been transformed in VRS

    Object.keys(this.state).forEach(function (key) {
      _this._vm.$watch(key, function (newValue, oldValue) {
        // call global hooks in order
        VueReactiveStore.globalPlugins.forEach(function (hook) {
          hook.state && hook.state.after && hook.state.after(_this.rootStore, _this.name + '.state.' + key, newValue, oldValue);
        });

        _this.plugins.forEach(function (hook) {
          hook.state && hook.state.after && hook.state.after(_this.rootStore, _this.name + '.state.' + key, newValue, oldValue);
        });
      }, {
        deep: true
      });
    }); // listen to computed properties recomputed
    // idem, doesn't work for modules

    Object.keys(this.computed).forEach(function (key) {
      _this._vm.$watch(key, function (newValue, oldValue) {
        // call global hooks in order
        VueReactiveStore.globalPlugins.forEach(function (hook) {
          hook.computed && hook.computed.after && hook.computed.after(_this.rootStore, _this.name + '.computed.' + key, newValue, oldValue);
        });

        _this.plugins.forEach(function (hook) {
          hook.computed && hook.computed.after && hook.computed.after(_this.rootStore, _this.name + '.computed.' + key, newValue, oldValue);
        });
      }, {
        deep: true
      });
    });
    /**
     * We add each module state/computed
     * in the store where they are added
     */

    Object.keys(this.subStores).forEach(function (moduleName) {
      _this.state[moduleName] = _this.subStores[moduleName].state; // this won't work, because in computed we have to store functions,
      // but here we're trying to store Objects... ? so we can't nest modules.computed
      // this.computed[moduleName] = this.subStores[moduleName].computed
    });
  }

  _createClass(VueReactiveStore, null, [{
    key: "registerPlugin",
    value: function registerPlugin(plugin) {
      if (VueReactiveStore.globalPlugins.indexOf(plugin) === -1) {
        VueReactiveStore.globalPlugins.push(plugin);
      } else {
        console.warn('You\'re trying to add a plugin already registered.');
      }
    }
  }]);

  return VueReactiveStore;
}();
/**
 * Global plugins, called each time :
 * * an action is trigerred (before / after hooks)
 * * a property of the state is mutated (after hook)
 * * a computed property has been recomputed (after hook)
 * * a prop property has changed (after hook, mutated out of the store himself)
 * * a watch triger has been trigerred (after hook)
 */

VueReactiveStore.globalPlugins = [];

export default VueReactiveStore;
export { VueReactiveStore };
